<template>
  <div
    class="tw-flex tw-flex-col tw-font-sans tw-min-h-screen tw-max-w-[100vw] tw-overflow-x-hidden"
  >
    <navbar :key="`${$i18n.locale}-navbar`" />

    <PageContainer>
      <nuxt />
    </PageContainer>

    <LoadingOverlay v-if="loading.includes('login')" />
    <Footer :key="`${$i18n.locale}-footer`" />
    <SimplifiedOnboarding v-if="!onboarded" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import jwt from 'jsonwebtoken'
import Footer from '@/components/Footer'
import LoadingOverlay from '@/components/shared/LoadingOverlay'
import Navbar from '@/components/navbar/Navbar'
import SimplifiedOnboarding from '~/components/onboarding/SimplifiedOnboarding'
import PageContainer from '@/components/shared/PageContainer'

export default {
  components: {
    Footer,
    Navbar,
    LoadingOverlay,
    PageContainer,
    SimplifiedOnboarding,
  },
  data() {
    return {
      onboarded: true,
    }
  },
  head() {
    return {
      htmlAttrs: {
        lang: this.$i18n.locale,
      },
    }
  },
  computed: {
    ...mapState('fiiAuth', ['loading', 'identity']),
  },
  mounted() {
    // this is needed for avoiding to break pages where auth is not required
    const skipOnboarding = this.$route.name
      ? this.$route.name.startsWith('fund')
      : false

    const namesPresent =
      Boolean(this.identity?.meta?.firstName) &&
      Boolean(this.identity?.meta?.lastName)
    const alreadyOnboarded =
      Object.keys(this.identity).length > 0
        ? !!this.identity.meta?.onboarded
        : true
    this.onboarded = skipOnboarding || (namesPresent && alreadyOnboarded)

    window.addEventListener('resize', () => {
      this.$store.commit('setWindowSize', window)
    })
    this.setupAnalytics()
    this.loadIntercom()
    this.loadAdaptativeDelivery()
  },
  methods: {
    async getUserHash() {
      const response = await this.$axios.$get(`/CORE_URL/profile/intercom`)
      return response.hash
    },
    setupAnalytics() {
      if (this.$store.state.fiiAuth.identity.entityUuid) {
        this.$analytics.setUserId(this.$store.state.fiiAuth.identity.entityUuid)
      }
    },
    async loadIntercom() {
      if (this.$cookies.get('auth._token.uptogether')) {
        const decodedAccessToken = jwt.decode(
          this.$cookies.get('auth._token.uptogether')
        )
        const userHash = await this.getUserHash()
        const fullName =
          decodedAccessToken.identity.meta.first_name ||
          decodedAccessToken.identity.meta.last_name
            ? decodedAccessToken.identity.meta.first_name +
              ' ' +
              decodedAccessToken.identity.meta.last_name
            : decodedAccessToken.identity.meta.email

        window.intercomSettings = {
          app_id: `${process.env.INTERCOM_APP_ID}`,
          name: fullName,
          user_id: decodedAccessToken.identity.entity_uuid,
          user_hash: userHash,
          email: decodedAccessToken.identity.meta.email,
          'Date Of Birth': decodedAccessToken.identity.meta.dob
            ? decodedAccessToken.identity.meta.dob
            : '',
          language: decodedAccessToken.identity.meta.language_preference
            ? decodedAccessToken.identity.meta.language_preference.language
              ? decodedAccessToken.identity.meta.language_preference.language
              : ''
            : '',
          language_override: this.$i18n.locale,
          phone: decodedAccessToken.identity.meta.phone
            ? decodedAccessToken.identity.meta.phone
            : '',
          'Entity URL': `https://ops.uptogether.org/entity/${decodedAccessToken.identity.entity_uuid}`,
          'Entity Support URL': `https://supporthq.uptogether.org/search/${decodedAccessToken.identity.entity_uuid}`,
          'Amplitude URL': `https://analytics.amplitude.com/uptogether/project/${process.env.AMPLITUDE_PROJECT_ID}/search/${decodedAccessToken.identity.entity_uuid}`,
        }
      } else {
        window.intercomSettings = {
          app_id: `${process.env.INTERCOM_APP_ID}`,
        }
      }

      // We pre-filled your app ID in the widget URL: 'https://widget.intercom.io/widget/drnlj5h7'
      ;(function () {
        const w = window
        const ic = w.Intercom
        if (typeof ic === 'function') {
          ic('reattach_activator')
          ic('update', w.intercomSettings)
        } else {
          const d = document
          const i = function () {
            i.c(arguments)
          }
          i.q = []
          i.c = function (args) {
            i.q.push(args)
          }
          w.Intercom = i
          const l = function () {
            const s = d.createElement('script')
            s.type = 'text/javascript'
            s.async = true
            s.src = `https://widget.intercom.io/widget/${process.env.INTERCOM_APP_ID}`
            const x = d.getElementsByTagName('script')[0]
            x.parentNode.insertBefore(s, x)
          }
          if (document.readyState === 'complete') {
            l()
          } else if (w.attachEvent) {
            w.attachEvent('onload', l)
          } else {
            w.addEventListener('load', l, false)
          }
        }
      })()

      try {
        window.Intercom('boot', {
          app_id: `${process.env.INTERCOM_APP_ID}`,
        })
        window.Intercom('update')
      } catch (e) {
        console.log('Error with intercom script: ', e)
      } finally {
        console.log('Finished intercom script')
      }
    },
    loadAdaptativeDelivery() {
      ;(function (src, cb) {
        const s = document.createElement('script')
        s.setAttribute('src', src)
        s.onload = cb
        ;(document.head || document.body).appendChild(s)
      })(
        'https://ucarecdn.com/libs/blinkloader/3.x/blinkloader.min.js',
        function () {
          // Validating if the library was properly added
          if (window && !window.Blinkloader) return

          window.Blinkloader.optimize({
            pubkey: process.env.UPLOADCARE_API_KEY,
            fadeIn: true,
            lazyload: true,
            smartCompression: true,
            responsive: true,
            retina: true,
            webp: true,
            batchSize: 10,
          })
        }
      )
    },
  },
}
</script>

<style scoped></style>
